import React, { memo, useMemo } from 'react';
import { cn, makeDataCy } from '../../../../helpers/util';

interface AddCauseConsequenceButtonProps {
  label: string;
  className?: string;
  hasCausesOrConsequences: boolean;
  controlsClassName: string;
  onClick?: () => void;
}

/**
 * A button component for adding causes and consequences in a flow diagram.
 *
 * @component
 * @param {string} label - The text label to display on the button
 * @param {string} controlsClassName - The CSS class name for custom styling when controls are present
 * @param {boolean} hasCausesOrConsequences - Flag indicating if there are causes or consequences present
 * @param {() => void} onClick - Click event handler function
 *
 * @returns {JSX.Element} A button element with conditional styling based on controls
 *
 * @example
 * ```tsx
 * <AddCauseConsequenceButton
 *   label="Add Cause"
 *   controlsClassName="custom-class"
 *   hasCausesOrConsequences={true}
 *   onClick={() => handleClick()}
 * />
 * ```
 */
const AddCauseConsequenceButton = ({
  label,
  hasCausesOrConsequences,
  controlsClassName,
  onClick,
}: AddCauseConsequenceButtonProps): React.JSX.Element => {
  // e.g. label = "Add Cause"  → slug = "add-cause"
  const dataCyLabel = useMemo(() => makeDataCy(label), [label]);

  return (
    <button
      data-cy={`${dataCyLabel}-button`}
      className={cn(
        'nopan nodrag bt-whitespace-nowrap',
        { [controlsClassName]: hasCausesOrConsequences },
        {
          'bt-absolute bt-bottom-0 bt-left-0 bt-right-0 bt-top-0 bt-m-auto bt-h-[50px] bt-w-[100px] bt-text-xs':
            !hasCausesOrConsequences,
        }
      )}
      onClick={onClick}
    >
      {label}
    </button>
  );
};

export default memo(AddCauseConsequenceButton);
